import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import LogLayout from "../components/LogLayout";
import { FileText, CreditCard, Truck, TrendingUp, AlertCircle, ArrowRight, Building2, Plus, Calendar, Wallet, Clock, AlertTriangle, Zap, Send as SendIcon } from "lucide-react";
import { Button } from "../components/ui/button";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import axios from "axios";
import { toast } from "sonner";
import { getAuthHeaders } from "../lib/auth";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const PERIODS = [
  { value: "1m", label: "Ce mois" },
  { value: "3m", label: "3 mois" },
  { value: "6m", label: "6 mois" },
  { value: "12m", label: "12 mois" },
  { value: "all", label: "Tout" },
];

const STATUS_COLORS = {
  editee: "bg-blue-100 text-blue-700 border-blue-300",
  deposee: "bg-cyan-100 text-cyan-700 border-cyan-300",
  rejetee: "bg-red-100 text-red-700 border-red-300",
  paiement_partiel: "bg-orange-100 text-orange-700 border-orange-300",
  paiement_total: "bg-emerald-100 text-emerald-700 border-emerald-300",
  paiement_effectue: "bg-green-100 text-green-700 border-green-300",
  archived: "bg-slate-100 text-slate-600 border-slate-300",
  // Legacy
  received: "bg-blue-100 text-blue-700 border-blue-300",
  deposited: "bg-cyan-100 text-cyan-700 border-cyan-300",
  in_recovery: "bg-yellow-100 text-yellow-700 border-yellow-300",
  partially_paid: "bg-orange-100 text-orange-700 border-orange-300",
  paid: "bg-emerald-100 text-emerald-700 border-emerald-300",
};

const STATUS_LABELS = {
  editee: "Éditées", deposee: "Déposées", rejetee: "Rejetées",
  paiement_partiel: "Paiement partiel", paiement_total: "Paiement total",
  paiement_effectue: "Paiement effectué", archived: "Archivées",
  // Legacy
  received: "Éditées", deposited: "Déposées", in_recovery: "En recouvrement",
  partially_paid: "Paiement partiel", paid: "Payées",
  pending: "En attente", picked_up: "Récupéré", in_transit: "En transit",
  delivered: "Livré", signed: "Décharge signée",
};

const formatFCFA = (n) => new Intl.NumberFormat("fr-FR").format(Math.round(n || 0)) + " FCFA";

export default function LogDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("all");
  const [forecastData, setForecastData] = useState(null);
  const [alertsData, setAlertsData] = useState(null);
  const [relancing, setRelancing] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const h = getAuthHeaders();
        const [dashRes, fcRes, alertsRes] = await Promise.all([
          axios.get(`${API}/log/dashboard?period=${period}`, { headers: h }),
          axios.get(`${API}/log/encaissements/summary`, { headers: h }),
          axios.get(`${API}/smart-alerts/check`, { headers: h }).catch(() => ({ data: null })),
        ]);
        setData(dashRes.data);
        setForecastData(fcRes.data);
        setAlertsData(alertsRes.data);
      } catch { }
      finally { setLoading(false); }
    };
    load();
  }, [period]);

  const handleRelance = async (invoiceId) => {
    setRelancing(invoiceId);
    try {
      const h = getAuthHeaders();
      const res = await axios.post(`${API}/smart-alerts/relance/${invoiceId}`, {}, { headers: h });
      toast.success(res.data.message);
      const alertsRes = await axios.get(`${API}/smart-alerts/check`, { headers: h });
      setAlertsData(alertsRes.data);
    } catch { toast.error("Erreur lors de la relance"); }
    finally { setRelancing(null); }
  };

  if (loading) return (
    <LogLayout title="Tableau de bord" subtitle="Vue d'ensemble de votre activite logistique">
      <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent" /></div>
    </LogLayout>
  );

  const inv = data?.invoices || {};
  const del_ = data?.deliveries || {};

  const kpis = [
    { label: "Factures totales", value: inv.total || 0, icon: FileText, color: "text-sky-600", bg: "bg-white border-sky-200 shadow-sm" },
    { label: "Montant a recouvrer", value: formatFCFA(inv.pending), icon: AlertCircle, color: "text-amber-600", bg: "bg-white border-amber-200 shadow-sm" },
    { label: "Taux de recouvrement", value: `${inv.recovery_rate || 0}%`, icon: TrendingUp, color: "text-emerald-600", bg: "bg-white border-emerald-200 shadow-sm" },
    { label: "Expéditions actives", value: del_.total || 0, icon: Truck, color: "text-violet-600", bg: "bg-white border-violet-200 shadow-sm" },
  ];

  return (
    <LogLayout title="Tableau de bord" subtitle="Vue d'ensemble de votre activite logistique">
      <div data-testid="log-dashboard" className="space-y-6">
        {/* Period filter + Quick Actions */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-xl p-1 shadow-sm">
            {PERIODS.map(p => (
              <button key={p.value} data-testid={`period-${p.value}`}
                onClick={() => setPeriod(p.value)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  period === p.value ? "bg-sky-500 text-white" : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                }`}>
                {p.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button data-testid="quick-new-invoice" onClick={() => navigate("/log/invoices")} variant="outline" className="border-sky-200 text-sky-600 hover:bg-sky-50 gap-2 text-xs">
              <Plus className="w-3 h-3" /> Nouvelle facture
            </Button>
            <Button data-testid="quick-new-insurer" onClick={() => navigate("/log/insurers")} variant="outline" className="border-sky-200 text-sky-600 hover:bg-sky-50 gap-2 text-xs">
              <Building2 className="w-3 h-3" /> Ajouter assureur
            </Button>
            <Button data-testid="quick-analytics" onClick={() => navigate("/log/analytics")} variant="outline" className="border-sky-200 text-sky-600 hover:bg-sky-50 gap-2 text-xs">
              <TrendingUp className="w-3 h-3" /> Voir analytics
            </Button>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {kpis.map((k, i) => (
            <div key={i} data-testid={`kpi-${i}`} className={`${k.bg} rounded-xl border p-4`}>
              <div className="flex items-center gap-2 mb-2">
                <k.icon className={`w-4 h-4 ${k.color}`} />
                <span className="text-[10px] text-slate-500 uppercase tracking-wide">{k.label}</span>
              </div>
              <p className={`text-2xl font-bold ${k.color}`}>{k.value}</p>
            </div>
          ))}
        </div>

        {/* Pipeline Visuel */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Pipeline des factures</h3>
          <div className="flex flex-wrap gap-2">
            {[
              { keys: ["received", "editee"], label: "Éditées" },
              { keys: ["deposited", "deposee"], label: "Déposées" },
              { keys: ["in_recovery", "en_recouvrement"], label: "En recouvrement" },
              { keys: ["partially_paid", "paiement_partiel"], label: "Paiement partiel" },
              { keys: ["paid", "paiement_effectue", "paiement_total"], label: "Payées" },
              { keys: ["archived"], label: "Archivées" },
            ].map((stage, i, arr) => {
              const count = stage.keys.reduce((sum, k) => sum + (inv.by_status?.[k] || 0), 0);
              return (
                <div key={i} className="flex items-center gap-2">
                  <div className={`px-3 py-1.5 rounded-lg border text-xs font-medium ${STATUS_COLORS[stage.keys[0]] || "bg-slate-100 text-slate-600 border-slate-200"}`}>
                    {stage.label} <span className="font-bold ml-1">{count}</span>
                  </div>
                  {i < arr.length - 1 && <ArrowRight className="w-3 h-3 text-slate-300" />}
                </div>
              );
            })}
          </div>
        </div>

        {/* Encaissements Widget */}
        {forecastData && forecastData.total_pending > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm" data-testid="encaissements-widget">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Wallet className="w-4 h-4 text-emerald-600" />
                <h3 className="text-sm font-semibold text-slate-700">Prévisions d'encaissement</h3>
                <span className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">
                  {forecastData.total_pending} en attente
                </span>
              </div>
              <button onClick={() => navigate("/log/creances")} className="text-[10px] text-sky-600 hover:text-sky-500 flex items-center gap-1" data-testid="view-all-forecasts">
                Détails <ArrowRight className="w-3 h-3" />
              </button>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-[10px] text-slate-500 mb-1">En retard</p>
                <p className="text-lg font-bold text-red-600">{forecastData.overdue?.count || 0}</p>
                <p className="text-[10px] text-red-500">{formatFCFA(forecastData.overdue?.amount || 0)}</p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-[10px] text-slate-500 mb-1">Cette semaine</p>
                <p className="text-lg font-bold text-amber-600">{forecastData.this_week?.count || 0}</p>
                <p className="text-[10px] text-amber-500">{formatFCFA(forecastData.this_week?.amount || 0)}</p>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                <p className="text-[10px] text-slate-500 mb-1">Ce mois</p>
                <p className="text-lg font-bold text-emerald-600">{forecastData.this_month?.count || 0}</p>
                <p className="text-[10px] text-emerald-500">{formatFCFA(forecastData.this_month?.amount || 0)}</p>
              </div>
              <div className="bg-sky-50 border border-sky-200 rounded-lg p-3">
                <p className="text-[10px] text-slate-500 mb-1">Total attendu</p>
                <p className="text-lg font-bold text-sky-600">{formatFCFA(forecastData.total_expected_amount || 0)}</p>
              </div>
            </div>
            {/* Treasury Forecast Chart */}
            {forecastData.treasury_forecast?.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="w-3.5 h-3.5 text-violet-500" />
                  <p className="text-xs font-semibold text-slate-600">Trésorerie prévisionnelle (12 semaines)</p>
                </div>
                <p className="text-[10px] text-slate-400 mb-3">Vert = Montant disponible par semaine | Violet = Cumul progressif des encaissements</p>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={forecastData.treasury_forecast} margin={{ top: 5, right: 10, left: 10, bottom: 20 }}>
                    <XAxis dataKey="week" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} label={{ value: "Semaines", position: "insideBottom", offset: -10, style: { fontSize: 10, fill: "#94a3b8" } }} />
                    <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} tickFormatter={v => v >= 1000000 ? `${(v/1000000).toFixed(1)}M` : v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} label={{ value: "FCFA", angle: -90, position: "insideLeft", style: { fontSize: 10, fill: "#94a3b8" } }} />
                    <Tooltip
                      formatter={(value, name) => [new Intl.NumberFormat("fr-FR").format(value) + " FCFA", name === "amount" ? "Disponible" : "Cumulé"]}
                      labelFormatter={(label) => {
                        const item = forecastData.treasury_forecast?.find(t => t.week === label);
                        return item ? `${label} (${item.period})` : label;
                      }}
                      contentStyle={{ background: "#fff", borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "12px" }}
                    />
                    <Bar dataKey="amount" fill="#10b981" radius={[4, 4, 0, 0]} name="Disponible" />
                    <Bar dataKey="cumulative" fill="#818cf8" radius={[4, 4, 0, 0]} name="Cumulé" opacity={0.4} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}

        {/* Smart Alerts Widget - Always visible */}
        {alertsData && (
          <div className={`bg-white rounded-xl border ${(alertsData.summary?.total_alerts > 0 || (alertsData.creance_alerts || []).length > 0) ? "border-amber-200" : "border-slate-200"} p-5 shadow-sm`} data-testid="smart-alerts-widget">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-amber-500" />
                <h3 className="text-sm font-semibold text-slate-700">Alertes Intelligentes</h3>
                {alertsData.summary?.critical > 0 && (
                  <span className="text-[10px] bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-bold animate-pulse">
                    {alertsData.summary.critical} critique(s)
                  </span>
                )}
              </div>
              <button onClick={() => navigate("/log/alertes")} className="text-[10px] text-sky-600 hover:text-sky-500 flex items-center gap-1" data-testid="view-all-alerts">
                Voir tout <ArrowRight className="w-3 h-3" />
              </button>
            </div>
            {alertsData.summary?.total_alerts > 0 ? (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-[10px] text-slate-500 mb-1">Alertes retard</p>
                    <p className="text-lg font-bold text-amber-600">{alertsData.summary.total_alerts}</p>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-[10px] text-slate-500 mb-1">Critiques</p>
                    <p className="text-lg font-bold text-red-600">{alertsData.summary.critical}</p>
                  </div>
                  <div className="bg-sky-50 border border-sky-200 rounded-lg p-3">
                    <p className="text-[10px] text-slate-500 mb-1">Montant a risque</p>
                    <p className="text-sm font-bold text-sky-600">{formatFCFA(alertsData.summary.total_amount_at_risk)}</p>
                  </div>
                  <div className="bg-violet-50 border border-violet-200 rounded-lg p-3">
                    <p className="text-[10px] text-slate-500 mb-1">Alertes créances</p>
                    <p className="text-lg font-bold text-violet-600">{alertsData.summary.creance_alerts_count}</p>
                  </div>
                </div>
                {(alertsData.alerts || []).slice(0, 3).map((alert) => {
                  const isCritical = alert.severity === "critical";
                  return (
                    <div key={alert.id} className={`flex items-center justify-between p-3 rounded-lg mb-2 border ${isCritical ? "bg-red-50 border-red-200" : "bg-amber-50 border-amber-200"}`}>
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <AlertTriangle className={`w-4 h-4 flex-shrink-0 ${isCritical ? "text-red-500" : "text-amber-500"}`} />
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-slate-800 truncate">{alert.insurer} - {alert.invoice_number}</p>
                          <p className="text-[10px] text-slate-500">{formatFCFA(alert.amount)} - {alert.days_waiting}j d'attente (+{alert.excess_days}j)</p>
                        </div>
                      </div>
                      <Button size="sm" onClick={() => handleRelance(alert.invoice_id)} disabled={relancing === alert.invoice_id}
                        data-testid={`dash-relance-${alert.invoice_id?.slice(0, 8)}`}
                        className={`text-xs ml-2 ${isCritical ? "bg-red-600 hover:bg-red-700 text-white" : "bg-amber-500 hover:bg-amber-600 text-white"}`}>
                        <SendIcon className="w-3 h-3 mr-1" />
                        {relancing === alert.invoice_id ? "..." : "Relancer"}
                      </Button>
                    </div>
                  );
                })}
              </>
            ) : (
              <div className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <Zap className="w-4 h-4 text-emerald-500" />
                </div>
                <div>
                  <p className="text-sm font-medium text-emerald-700">Aucune alerte active</p>
                  <p className="text-[10px] text-emerald-600">Tous vos assureurs sont dans les delais de paiement. Le systeme surveille automatiquement les retards.</p>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Factures recentes */}
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-700">Factures recentes</h3>
              <button onClick={() => navigate("/log/invoices")} className="text-[10px] text-sky-600 hover:text-sky-500 flex items-center gap-1" data-testid="view-all-invoices">
                Tout voir <ArrowRight className="w-3 h-3" />
              </button>
            </div>
            {(data?.recent_invoices || []).length === 0 ? (
              <p className="text-xs text-slate-400 py-4 text-center">Aucune facture. Commencez par en ajouter une.</p>
            ) : (
              <div className="space-y-2">
                {data.recent_invoices.map((inv) => (
                  <div key={inv.id} className="flex items-center justify-between p-2.5 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors border border-slate-100">
                    <div>
                      <p className="text-xs font-medium text-slate-800">{inv.invoice_number}</p>
                      <p className="text-[10px] text-slate-500">{inv.insurer_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs font-bold text-emerald-600">{formatFCFA(inv.amount)}</p>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded border ${STATUS_COLORS[inv.status]}`}>
                        {STATUS_LABELS[inv.status]}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Top assureurs */}
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-700">Top assureurs</h3>
              <button onClick={() => navigate("/log/insurers")} className="text-[10px] text-sky-600 hover:text-sky-500 flex items-center gap-1" data-testid="view-all-insurers">
                Gerer <ArrowRight className="w-3 h-3" />
              </button>
            </div>
            {(data?.top_insurers || []).length === 0 ? (
              <p className="text-xs text-slate-400 py-4 text-center">Aucun assureur enregistré.</p>
            ) : (
              <div className="space-y-2">
                {data.top_insurers.map((ins, i) => {
                  const rate = ins.total > 0 ? Math.round(ins.paid / ins.total * 100) : 0;
                  return (
                    <div key={i} className="flex items-center justify-between p-2.5 rounded-lg bg-slate-50 border border-slate-100">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-3.5 h-3.5 text-sky-500" />
                        <div>
                          <p className="text-xs font-medium text-slate-800">{ins.name}</p>
                          <p className="text-[10px] text-slate-500">{ins.count} facture(s)</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs font-bold text-emerald-600">{formatFCFA(ins.total)}</p>
                        <div className="w-16 bg-slate-200 rounded-full h-1.5 mt-1">
                          <div className="bg-emerald-500 h-1.5 rounded-full" style={{ width: `${rate}%` }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </LogLayout>
  );
}
